/**
 * Initializes the select wish list overlay.
 */
import {
  addItemToWishList,
  createNewWishlist,
  deleteItemFromWishList,
  getAllWishlists
} from '../../../api/wish-list-api';
import { Icon } from '../../../general/icons/Icon';
import { createHtmlElementFromString } from '../../../utils/html.utils';
import { overlay as overlayModule } from '../overlay.module';
import { input as inputModule } from '../../../components/form-controls/input/input.module';
import { HamiltonProductList, wishList } from '../../wish-list/wish-list.module';
import { currentBreakpoint } from '../../../utils/resize.utils';
import { toastNotification } from '../../toast-notification/toast-notification.module';
import { wishListData } from '../../../wish-list-data.module';
import { eCatalog } from '../../e-catalog/e-catalog.module';

class SelectWishListOverlay {
  /**
   * ID of the select wish list overlay.
   */
  overlayId = 'select-wish-list-overlay';

  /**
   * First part of the markup for one row in a list, that displays all available wish lists. This is the checkbox/input
   * that always needs a label, which is defined below.
   */
  wishListToggleInputMarkup =
    '<input class="list-selection__item" type="checkbox" id="" name="select-wish-list-toggles">';

  /**
   * Second part of the markup for one row in a list, that displays all available wish lists. This is the visible label
   * that always needs an input, which is defined above.
   */
  wishListToggleLabelMarkup = `<label for="">${Icon.checkMark}</label>`;

  /**
   * Method that is called every time when the 'select wish list overlay' will be shown. This method loads the current
   * wish lists and displays them to the user.
   * @param overlay The overlay wrapper element
   */
  setup() {
    console.log('Setting up select wish list overlay');
    document.addEventListener('selectWishListOverlayInitialize', (event: CustomEvent) => {
      console.log('Initializing select wish list overlay');
      this.initialize(event.detail);
    });

    document.addEventListener('selectWishListOverlayInitializeLoad', (event: CustomEvent) => {
        console.log('Initializing select wish list overlay load content');
        this.loadContent(event.detail);
    });
  }

  loadContent(overlay: HTMLElement): void {
    const wishListButtonsContainer = overlay.querySelector('.list-selection');

    // render a loading state until the wish lists are loaded
    this.renderLoadingState(overlay, wishListButtonsContainer);

    // retrieve all wish lists of the user
    getAllWishlists()
      .then((wishLists) => {
        // remove previous wish lists or loading state
        wishListButtonsContainer.innerHTML = '';

        // ID of the product that should be added to the wish lists that the user selects
        // This ID is set by the e-catalog-tile module to the 'pin button' and later
        // set to the overlay element by the overlay module.
        const productId = overlay.dataset.overlayAssociatedId;

        for (const wishList of wishLists) {
          // create toggles for each wish list
          this.createToggleForWishList(wishList, productId, wishListButtonsContainer);
        }
      })
      .catch(() => {
        const errorMessage = overlay.dataset.errorMessage;
        toastNotification.createErrorToastMessage(errorMessage);
        overlayModule.close(overlay);
      });
  }

  /**
   * Render the loading state of the select wish list dialog.
   * @param overlay The overlay wrapper element
   * @param wishListButtonsContainer: Skeletons will be added to this container
   */
  renderLoadingState(overlay: HTMLElement, wishListButtonsContainer: Element): void {
    // remove all previous wish lists
    wishListButtonsContainer.innerHTML = '';

    for (let i = 0; i < 2; i++) {
      const wishListToggleLabelElem = createHtmlElementFromString(this.wishListToggleLabelMarkup);
      const wishListToggleInputElem = createHtmlElementFromString(this.wishListToggleInputMarkup) as HTMLInputElement;
      wishListToggleLabelElem.classList.add(i === 0 ? 'list-selection__skeleton' : 'list-selection__skeleton-delay');
      wishListButtonsContainer.appendChild(wishListToggleInputElem);
      wishListButtonsContainer.appendChild(wishListToggleLabelElem);
    }
  }

  /**
   * Create a toggle for each of the wish lists, preselect a list, if it already includes the product
   * @param wishList: The wish list data
   * @param productId: ID of the product that should be added to the wish list
   * @param wishListButtonsContainer: Toggles will be added to this container
   */
  createToggleForWishList(wishList: HamiltonProductList, productId: string, wishListButtonsContainer: Element): void {
    const wishListId = wishList['@name'];

    // a row consists of an input and an associated label
    const wishListToggleInputElem = createHtmlElementFromString(this.wishListToggleInputMarkup) as HTMLInputElement;
    const wishListToggleLabelElem = createHtmlElementFromString(this.wishListToggleLabelMarkup);
    wishListToggleInputElem.id = wishListId;
    wishListToggleLabelElem.setAttribute('for', wishListId);
    const textNode = document.createTextNode(wishList.name);

    // Insert the text before the check mark icon instead of replacing the content (which would override the icon)
    wishListToggleLabelElem.insertBefore(textNode, wishListToggleLabelElem.querySelector('svg'));

    const listItemIds = wishList.items?.map((item) => item['@id']);
    if (listItemIds && listItemIds.includes(productId)) {
      // highlight which list already contains the product
      wishListToggleInputElem.checked = true;
    }

    // append them to the toggle section
    wishListButtonsContainer.appendChild(wishListToggleInputElem);
    wishListButtonsContainer.appendChild(wishListToggleLabelElem);
  }

  /**
   * Basic initialization when the select-wish-list overlay is created. Called by the general overlay component.
   * Sets the listeners for click events on e.g. the save button.
   * @param overlay The overlay wrapper element
   */
  initialize(overlay: HTMLElement): void {
    // listen for clicks on the save button
    overlay.querySelector('.select-wish-list-overlay-save-button').addEventListener('click', () => {
      this.saveSelectionClicked(overlay);
    });

    const wishListNameInput = overlay.querySelector<HTMLInputElement>('#select-wish-list-input');
    const createListButton = overlay.querySelector<HTMLButtonElement>('.select-wish-list-overlay-create-list-button');

    // listen for changes in the text field
    wishListNameInput.addEventListener('input', () => {
      const currentInput = wishListNameInput.value;

      // if the list name is valid, enable the 'create list' button
      if (currentInput && currentInput.length > 0) {
        createListButton.removeAttribute('disabled');
      } else {
        createListButton.setAttribute('disabled', 'true');
      }
    });

    // listen for clicks on the 'create list' button
    createListButton.addEventListener('click', () => {
      // disable the button so that the user does not click twice
      createListButton.disabled = true;

      const newListName = wishListNameInput.value;
      createNewWishlist(newListName).then((success) => {
        if (success) {
          // reload
          this.loadContent(overlay);

          // clear input field
          wishListNameInput.value = '';
          inputModule.resetInput(wishListNameInput);
        } else {
          const errorMessage = overlay.dataset.errorMessage;
          toastNotification.createErrorToastMessage(errorMessage);
        }

        // enable the button again
        createListButton.disabled = false;
      });
    });
  }

  /**
   * User clicked on the save button of the overlay. Now save the selection (add/remove the product
   * to the selected wish lists)
   */
  saveSelectionClicked(overlay: HTMLElement): void {
    // first disable the save button, so that user does not click twice
    const createListButton = overlay.querySelector<HTMLButtonElement>('.select-wish-list-overlay-save-button');
    createListButton.disabled = true;

    // check which wish lists were selected by the user
    const chosenOptions = this.getSelectedWishListIds(overlay);
    // ID of the product that should be added to the selected wish lists
    const productId = overlay.dataset.overlayAssociatedId;
    // Get product information depending on page (product list or product detail)
    const product = document.querySelector(`[data-product-id="${productId}"]`);
    const productName = product
      ? product.querySelector<HTMLElement>('.tile__headline').innerText
      : document.querySelector<HTMLElement>('.product-detail__headline').innerText;
    const partNumber = product
      ? product.querySelector<HTMLElement>('.tile__subtitle').innerText
      : document.querySelector<HTMLElement>('.product-detail__id').innerText;
    const imageSmall = product
      ? product.querySelector<HTMLImageElement>('img')?.src
      : document.querySelector<HTMLImageElement>('.text-and-image-component__image-wrapper img')?.src;

    const fallbackIcons = product
      ? product.getAttribute('data-fallback-icons')
      : document
          .querySelector<HTMLImageElement>('.text-and-image-component__image-wrapper')
          ?.getAttribute('data-fallback-icons');

    // Labels to show in the toast messages
    const labelWishlistAdded = overlay.dataset.labelWishlistAdded;
    const labelWishlistRemoved = overlay.dataset.labelWishlistRemoved;
    const labelWishlistGoTo = overlay.dataset.labelWishlistGoTo;
    let added;

    // First retrieve the current wish lists and which products they contain. This
    // is necessary because when removing/adding an item to a list, we need to specify
    // all products from that wish list.
    getAllWishlists()
      .then((wishLists) => {
        // we need to update multiple wish lists (each list that we are adding/removing an item)
        // let's create a promise for each list we are updating and then waiting for all of them to finish
        const wishListPromises = [];
        for (const wishList of wishLists) {
          // items currently in this wish list
          const currentItemIds = wishList.items?.map((item) => item['@id']) || [];

          // Should the selected product be part of this wish list? Depends on the users selection in the overlay
          const shouldIncludeProduct = chosenOptions.find(
            (item) => item.wishListId === wishList['@name'] && item.include
          );

          // If the item is currently part of the wish list, this helps us determine if we need to remove/add it
          const isCurrentlyInWishList = currentItemIds.includes(productId);

          // now add or remove the product to the wish list
          if (shouldIncludeProduct && !isCurrentlyInWishList) {
            added = true;
            wishListPromises.push(addItemToWishList(productId, currentItemIds, wishList['@name']));
            wishListData.productIdsInWishLists.push(productId);
          } else if (!shouldIncludeProduct && isCurrentlyInWishList) {
            added = false;
            wishListPromises.push(deleteItemFromWishList(productId, currentItemIds, wishList['@name']));

            // only if no other wish list contains this item now
            wishListData.removeProductFromCachedWishList(productId);
          }

          // now update the pin buttons that may be visible to the user already
          eCatalog.eCatalogProducts.updatePinButtonStates();
          eCatalog.eCatalogDetail.updatePinAndShoppingCartButtonState();
        }

        // wait for all wish list updates to finish
        Promise.allSettled(wishListPromises).then((results) => {
          const success = results.find((promise) => promise.status === 'rejected') === undefined;

          // activate save button again
          createListButton.disabled = false;

          if (success) {
            // close the overlay, if all updates were successful
            overlayModule.close(overlay);

            if (added !== undefined) {
              const breakpoint = currentBreakpoint();
              const fallbackIconArray = fallbackIcons ? JSON.parse(fallbackIcons) : null;
              const product = { productName, partNumber, imageSmall, nodeid: productId, icons: fallbackIconArray };
              const labelText = added ? labelWishlistAdded : labelWishlistRemoved;
              if (breakpoint.isMobilePortrait || breakpoint.isMobileLandscape) {
                // on mobile show a toast
                toastNotification.createMobileToast(product, labelText, labelWishlistGoTo, () => {
                  wishList.openWishList();
                });
              } else {
                // Show toastify toast on desktop
                toastNotification.createDesktopToast(product, labelText);
              }
            }
          } else {
            const errorMessage = overlay.dataset.errorMessage;
            toastNotification.createErrorToastMessage(errorMessage);
          }
        });
      })
      .catch(() => {
        const errorMessage = overlay.dataset.errorMessage;
        toastNotification.createErrorToastMessage(errorMessage);
        overlayModule.close(overlay);
      });
  }

  /**
   * Returns the IDs of the wish lists that the user selected in the overlay.
   */
  getSelectedWishListIds(overlay: HTMLElement): Array<{ wishListId: string; include: boolean }> {
    const allToggleButtons = overlay.querySelectorAll<HTMLInputElement>('input[type=checkbox]');
    const chosenOptions: Array<{ wishListId: string; include: boolean }> = [];

    for (const inputElem of allToggleButtons) {
      chosenOptions.push({ wishListId: inputElem.id, include: inputElem.checked });
    }
    return chosenOptions;
  }
}

const selectWishListOverlayModule = new SelectWishListOverlay();
export { selectWishListOverlayModule as selectWishListOverlay };
